import React, { useState, useEffect, useRef } from 'react';
import './pagamento.css';
import { sendMessage } from '../../functions/apiCalls/api';
import { bffExecuteInsuranceAction } from '../../functions/apiCalls/bffService';

const Pagamento = ({
    message,
    client_id,
    marca = 'AM',
    origem = 'chat-web',
    messages = [],
    setMessages,
    localizacao = null,
    operational_system = 'web',
    version_app = '1.0.0',
    requestId,
    setRequestId,
    requestImage
}) => {
    const [isModalOpen, setIsModalOpen] = useState(true);
    const [visible, setVisible] = useState(false);
    const didReplaceLastMessageRef = useRef(false);
    const didAttemptPaymentRef = useRef(false);
    const didSendPaymentClosedRef = useRef(false);
    const didDispatchPaymentOpenRef = useRef(false);
    const didDispatchPaymentCloseRef = useRef(false);

    const [cardName, setCardName] = useState('');
    const [cardNumber, setCardNumber] = useState('');
    const [cpf, setCpf] = useState('');
    const [expiry, setExpiry] = useState('');
    const [cvv, setCvv] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitErrorMessage, setSubmitErrorMessage] = useState('');
    const [animatePaymentError, setAnimatePaymentError] = useState(false);
    const [submitSuccessMessage, setSubmitSuccessMessage] = useState('');
    const [animatePaymentSuccess, setAnimatePaymentSuccess] = useState(false);

    const [installmentsLoading, setInstallmentsLoading] = useState(false);
    const [installmentsError, setInstallmentsError] = useState('');
    const [installmentsOptions, setInstallmentsOptions] = useState([]);
    const [selectedInstallments, setSelectedInstallments] = useState(1);
    const [installmentsOpen, setInstallmentsOpen] = useState(false);
    const installmentsDropdownRef = useRef(null);

    // Relação pagador/paciente e steps internos
    // null = ainda não respondeu; 'same' = mesmo paciente; 'different' = outra pessoa
    const [payerRelation, setPayerRelation] = useState(null);
    const [isAddressStep, setIsAddressStep] = useState(false);

    // Dados adicionais do pagador quando não é o paciente
    const [payerBirthDate, setPayerBirthDate] = useState('');
    const [payerPhone, setPayerPhone] = useState('');
    const [addressCep, setAddressCep] = useState('');
    const [addressStreet, setAddressStreet] = useState('');
    const [addressNumber, setAddressNumber] = useState('');
    const [addressComplement, setAddressComplement] = useState('');
    const [addressNeighborhood, setAddressNeighborhood] = useState('');
    const [addressCity, setAddressCity] = useState('');
    const [addressState, setAddressState] = useState('');
    const [cepLoading, setCepLoading] = useState(false);
    const [cepError, setCepError] = useState('');

    // Animação de entrada e controle de scroll, reaproveitando o mesmo padrão do BuscaExames
    useEffect(() => {
        if (isModalOpen) {
            setTimeout(() => {
                setVisible(true);
            }, 10);

            document.body.classList.add('modal-open');
            document.body.style.overflow = 'hidden';
        } else {
            setVisible(false);
            document.body.classList.remove('modal-open');
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.classList.remove('modal-open');
            document.body.style.overflow = 'unset';
        };
    }, [isModalOpen]);

    // Sinalizar globalmente que a modal de pagamento está aberta/fechada (para bloquear CSAT).
    // Importante: o componente pode continuar montado mesmo quando "fecha" (renderizando null),
    // então precisamos disparar o evento também quando isModalOpen muda.
    useEffect(() => {
        try {
            if (typeof window === 'undefined') return;

            if (isModalOpen) {
                window.isPaymentModalOpen = true;
                if (!didDispatchPaymentOpenRef.current) {
                    didDispatchPaymentOpenRef.current = true;
                    didDispatchPaymentCloseRef.current = false;
                    window.dispatchEvent(new Event('payment-modal-opened'));
                }
            } else {
                window.isPaymentModalOpen = false;
                if (!didDispatchPaymentCloseRef.current && didDispatchPaymentOpenRef.current) {
                    didDispatchPaymentCloseRef.current = true;
                    window.dispatchEvent(new Event('payment-modal-closed'));
                }
            }
        } catch (e) {
            // ignore
        }
    }, [isModalOpen]);

    // Ao abrir o componente de pagamento, substituir a última mensagem por "Digitando..."
    // (mantendo os campos originais, como valorPagamento/cliente/idFluxoAgendamento/etc.)
    useEffect(() => {
        if (didReplaceLastMessageRef.current) return;
        if (typeof setMessages !== 'function') return;

        didReplaceLastMessageRef.current = true;
        setMessages((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) return prev;

            const last = prev[prev.length - 1];
            const lastIsTyping =
                last?.typing === true ||
                (last?.origin === 'bot' && typeof last?.response === 'string' && last.response === 'Digitando...');

            if (lastIsTyping) return prev;

            const next = prev.slice(0, -1);
            next.push({
                ...last,
                origin: 'bot',
                response: 'Digitando...',
                options: [],
                typing: true,
                bloqueiaInput: true
            });
            return next;
        });
    }, [setMessages]);

    // Fechar dropdown de parcelas ao clicar fora
    useEffect(() => {
        const onClickOutside = (e) => {
            if (installmentsDropdownRef.current && !installmentsDropdownRef.current.contains(e.target)) {
                setInstallmentsOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const monetaryAmount = (() => {
        const v = message?.valorPagamento;
        if (v === undefined || v === null || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    })();

    const formatBRL = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n)) return '';
        return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const selectedInstallmentsLabel = (() => {
        const n = Number(selectedInstallments) || 1;
        const found = installmentsOptions.find((o) => o.count === n);
        if (found?.label) return found.label;
        if (monetaryAmount) return `1x de ${formatBRL(monetaryAmount)}`;
        return 'Selecione';
    })();

    useEffect(() => {
        if (!isModalOpen) return;
        if (!monetaryAmount || monetaryAmount <= 0) {
            setInstallmentsOptions([]);
            setSelectedInstallments(1);
            setInstallmentsError('');
            return;
        }

        const controller = new AbortController();

        const load = async () => {
            try {
                setInstallmentsLoading(true);
                setInstallmentsError('');

                const context = {
                    action: 'CheckInstallmentsAction',
                    brand: 17,
                    monetaryAmount: monetaryAmount
                };

                let data;
                if (process.env.REACT_APP_BFF_API_URL || !process.env.REACT_APP_INSTALLMENTS_API_KEY) {
                    data = await bffExecuteInsuranceAction(context, '');
                } else {
                    const apiUrl =
                        process.env.REACT_APP_INSTALLMENTS_API_URL ||
                        'https://75d4xc23kztwvkvo4mgknjus3e0bkwqv.lambda-url.us-east-1.on.aws/api/v1/action/execute';
                    const apiKey =
                        process.env.REACT_APP_INSTALLMENTS_API_KEY ||
                        '123';

                    const resp = await fetch(apiUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-api-key': apiKey
                        },
                        body: JSON.stringify({ context, code_execute: '' }),
                        signal: controller.signal
                    });

                    if (!resp.ok) {
                        throw new Error(`Erro ao consultar parcelas: ${resp.status}`);
                    }

                    data = await resp.json();
                }
                const inst = data?.context?.installments;
                const maxInstallmentsRaw = inst?.maxInstallments ?? inst?.installments;
                const minValueInstallments = Number(inst?.minValueInstallments ?? 0);
                const maxInstallments = Number(maxInstallmentsRaw);

                if (!Number.isFinite(maxInstallments) || maxInstallments < 1) {
                    throw new Error('Resposta de parcelas inválida');
                }

                const opts = [];
                for (let i = 1; i <= maxInstallments; i++) {
                    const per = monetaryAmount / i;
                    if (
                        i > 1 &&
                        Number.isFinite(minValueInstallments) &&
                        minValueInstallments > 0 &&
                        per < minValueInstallments
                    ) {
                        continue;
                    }
                    opts.push({ count: i, label: `${i}x de ${formatBRL(per)}` });
                }

                const safeOpts = opts.length > 0 ? opts : [{ count: 1, label: `1x de ${formatBRL(monetaryAmount)}` }];
                setInstallmentsOptions(safeOpts);
                setSelectedInstallments((prev) => {
                    const prevN = Number(prev);
                    const exists = safeOpts.some((o) => o.count === prevN);
                    return exists ? prevN : 1;
                });
            } catch (e) {
                if (e?.name === 'AbortError') return;
                console.error(e);
                setInstallmentsOptions([{ count: 1, label: `1x de ${formatBRL(monetaryAmount)}` }]);
                setSelectedInstallments(1);
                setInstallmentsError('Não foi possível carregar as opções de parcelamento.');
            } finally {
                setInstallmentsLoading(false);
            }
        };

        load();
        return () => controller.abort();
    }, [isModalOpen, monetaryAmount]);

    const closeModal = () => {
        setVisible(false);
        setTimeout(() => {
            setIsModalOpen(false);
        }, 300);
    };

    const handleUserClose = () => {
        // Se o usuário fechar sem tentar realizar o pagamento, avisar o conversation
        if (!didAttemptPaymentRef.current && !didSendPaymentClosedRef.current) {
            didSendPaymentClosedRef.current = true;
            try {
                sendMessage(
                    setMessages,
                    client_id,
                    'PAYMENT-CLOSED',
                    messages,
                    marca,
                    origem,
                    null,
                    localizacao,
                    null,
                    null,
                    null,
                    null,
                    null,
                    operational_system,
                    version_app,
                    null,
                    null,
                    requestImage,
                    setRequestId
                );
            } catch (e) {
                // ignore
            }
        }

        closeModal();
    };

    // Helpers de formatação
    const formatCardNumberForInput = (value) => {
        const digits = value.replace(/\D/g, '').slice(0, 16);
        return digits.replace(/(.{4})/g, '$1 ').trim();
    };

    const formatCpfForInput = (value) => {
        const digits = value.replace(/\D/g, '').slice(0, 11);
        return digits;
    };

    const formatExpiryForInput = (value) => {
        const digits = value.replace(/\D/g, '').slice(0, 4);
        if (digits.length <= 2) return digits;
        return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    };

    const formatCvvForInput = (value) => {
        const digits = value.replace(/\D/g, '').slice(0, 4);
        return digits;
    };

    const formatBirthDateForInput = (value) => {
        const raw = value.replace(/\D/g, '').slice(0, 8);
        if (raw.length <= 2) return raw;
        if (raw.length <= 4) {
            return raw.replace(/(\d{2})(\d{0,2})/, '$1/$2');
        }
        return raw.replace(/(\d{2})(\d{2})(\d{0,4})/, '$1/$2/$3');
    };

    const formatPhoneForInput = (value) => {
        const raw = value.replace(/\D/g, '').slice(0, 11);
        if (!raw) return '';

        // (DD
        if (raw.length <= 2) {
            return `(${raw}`;
        }

        // (DD) NNNN ou (DD) NNNNN (sem traço ainda)
        if (raw.length <= 6) {
            return `(${raw.slice(0, 2)}) ${raw.slice(2)}`;
        }

        // (DD) NNNN-NNNN ou (DD) NNNNN-NNNN
        if (raw.length <= 10) {
            return `(${raw.slice(0, 2)}) ${raw.slice(2, raw.length - 4)}-${raw.slice(raw.length - 4)}`;
        }

        // 11 dígitos: (DD) 9NNNN-NNNN
        return `(${raw.slice(0, 2)}) ${raw.slice(2, 7)}-${raw.slice(7, 11)}`;
    };

    const displayCardNumber = () => {
        const digits = cardNumber.replace(/\D/g, '').slice(0, 16);
        if (!digits) return '•••• •••• •••• ••••';
        const padded = (digits + '****************').slice(0, 16);
        return padded.replace(/(.{4})/g, '$1 ').trim();
    };

    const displayExpiry = () => {
        if (!expiry) return 'MM/AA';
        return expiry;
    };

    const displayName = () => {
        if (!cardName) return 'NOME DO TITULAR';
        return cardName.toUpperCase();
    };

    // Detecta a bandeira do cartão a partir dos dígitos
    const detectCardBrand = () => {
        const digits = cardNumber.replace(/\D/g, '');
        if (!digits) return null;

        // Prefixos específicos devem ser checados antes dos genéricos

        // Elo
        const eloPrefixes = ['4011', '4312', '4389', '4514', '4576', '5041', '5066', '5090', '6277', '6362', '6363', '5094'];
        if (eloPrefixes.some(prefix => digits.startsWith(prefix))) {
            return 'elo';
        }

        // Hipercard
        const hipercardPrefixes = ['3841', '3842', '6062'];
        if (hipercardPrefixes.some(prefix => digits.startsWith(prefix))) {
            return 'hipercard';
        }

        // American Express
        if (digits.startsWith('34') || digits.startsWith('37')) {
            return 'amex';
        }

        // Discover
        if (digits.startsWith('6011')) return 'discover';
        if (digits.startsWith('65')) return 'discover';
        const first3 = parseInt(digits.slice(0, 3), 10);
        if (!Number.isNaN(first3) && first3 >= 644 && first3 <= 649) {
            return 'discover';
        }

        // JCB (3528–3589)
        const first4 = parseInt(digits.slice(0, 4), 10);
        if (!Number.isNaN(first4) && first4 >= 3528 && first4 <= 3589) {
            return 'jcb';
        }

        // Mastercard (51–55 ou 2221–2720)
        const first2 = parseInt(digits.slice(0, 2), 10);
        if (!Number.isNaN(first2) && first2 >= 51 && first2 <= 55) {
            return 'mastercard';
        }
        const first4Mc = first4;
        if (!Number.isNaN(first4Mc) && first4Mc >= 2221 && first4Mc <= 2720) {
            return 'mastercard';
        }

        // Visa (começa com 4, mas depois de checar Elo)
        if (digits.startsWith('4')) {
            return 'visa';
        }

        return null;
    };

    const cardBrand = detectCardBrand();

    const getCardLogoSrc = () => {
        switch (cardBrand) {
            case 'visa':
                return '/vila-logo.png';
            case 'mastercard':
                return '/Mastercard-logo.png';
            case 'amex':
                return '/American-Express-Logo.png';
            case 'elo':
                return '/elo-logo.png';
            case 'hipercard':
                return '/Hipercard_logo.svg.png';
            case 'discover':
                return '/Discover-logo.png';
            case 'jcb':
                return '/JCB_logo.svg.png';
            default:
                return null;
        }
    };

    const getCardBrandLabel = () => {
        switch (cardBrand) {
            case 'visa':
                return 'Visa';
            case 'mastercard':
                return 'Mastercard';
            case 'amex':
                return 'American Express';
            case 'elo':
                return 'Elo';
            case 'hipercard':
                return 'Hipercard';
            case 'discover':
                return 'Discover';
            case 'jcb':
                return 'JCB';
            default:
                return marca.toUpperCase();
        }
    };

    const isFormValid = () => {
        const numberDigits = cardNumber.replace(/\D/g, '');
        const cpfDigits = cpf.replace(/\D/g, '');
        const expiryDigits = expiry.replace(/\D/g, '');
        const cvvDigits = cvv.replace(/\D/g, '');

        return (
            cardName.trim().length >= 3 &&
            numberDigits.length === 16 &&
            cpfDigits.length === 11 &&
            expiryDigits.length === 4 &&
            (cvvDigits.length === 3 || cvvDigits.length === 4) &&
            Number.isFinite(Number(selectedInstallments)) &&
            Number(selectedInstallments) >= 1 &&
            (installmentsOptions.length === 0 || installmentsOptions.some((o) => o.count === Number(selectedInstallments)))
        );
    };

    // Validação simples da data de nascimento (apenas obrigatório quando pagador != paciente)
    const isBirthDateValid = () => {
        if (payerRelation !== 'different') return true;
        return payerBirthDate.trim().length > 0;
    };

    const isFirstStepValid = () => {
        return isFormValid() && isBirthDateValid();
    };

    const isAddressValid = () => {
        if (payerRelation !== 'different' || !isAddressStep) return true;

        const cepDigits = (addressCep || '').replace(/\D/g, '');
        const hasCep = cepDigits.length === 8;

        return (
            hasCep &&
            (addressStreet || '').trim().length > 0 &&
            (addressNumber || '').trim().length > 0 &&
            (addressNeighborhood || '').trim().length > 0 &&
            (addressCity || '').trim().length > 0 &&
            (addressState || '').trim().length >= 2
        );
    };

    const handleCepLookup = async () => {
        const raw = addressCep || '';
        const onlyDigits = raw.replace(/\D/g, '').slice(0, 8);

        if (!onlyDigits || onlyDigits.length !== 8) {
            setCepError('Informe um CEP válido com 8 dígitos.');
            return;
        }

        const url = process.env.REACT_APP_INSURANCE_API_URL;
        const apiKey = process.env.REACT_APP_INSURANCE_API_KEY;

        if (!url || !apiKey) {
            setCepError('Serviço de busca de CEP indisponível no momento.');
            return;
        }

        try {
            setCepLoading(true);
            setCepError('');

            const context = {
                action: 'checkCepMovelHealthBookingAction',
                brandId: 17,
                agendamento: {
                    cep: onlyDigits
                }
            };

            let data;
            if (process.env.REACT_APP_BFF_API_URL || !apiKey) {
                data = await bffExecuteInsuranceAction(context, '');
            } else {
                const body = {
                    context,
                    code_execute: ''
                };

                const resp = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': apiKey
                    },
                    body: JSON.stringify(body)
                });

                if (!resp.ok) {
                    throw new Error(`HTTP ${resp.status}`);
                }

                data = await resp.json();
            }

            console.log('🔎 Resposta da busca de CEP (pagamento):', data);

            const address = data?.context?.address || {};
            const actionResult = data?.context?.actionResult || {};

            // Se a API indicar sucesso no CEP, preenchemos os campos automaticamente.
            if (address?.status === true && actionResult?.success === true && data?.returnCode === 0) {
                setAddressStreet(address.street || '');
                setAddressNeighborhood(address.neighborhood || '');
                setAddressCity(address.city || '');
                setAddressState(address.state || '');

                // Atualiza o campo de CEP com o formato retornado (se existir),
                // mas continua aceitando edição manual pelo usuário depois.
                if (address.postalCode) {
                    setAddressCep(String(address.postalCode));
                }

                setCepError('');
            } else {
                setCepError('CEP não encontrado. Você pode preencher o endereço manualmente.');
            }
        } catch (e) {
            console.error('❌ Erro ao buscar CEP para pagamento:', e);
            setCepError('Não foi possível buscar o CEP. Tente novamente.');
        } finally {
            setCepLoading(false);
        }
    };

    const handleSubmit = () => {
        if (
            !isFormValid() ||
            isSubmitting ||
            (payerRelation === 'different' && isAddressStep && !isAddressValid())
        ) {
            return;
        }
        didAttemptPaymentRef.current = true;

        const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        const triggerPaymentErrorAnimation = (msg) => {
            setSubmitErrorMessage(msg || 'Não foi possível processar o pagamento. Tente novamente.');
            setAnimatePaymentError(false);
            // força reflow do estado para reiniciar animação
            setTimeout(() => setAnimatePaymentError(true), 10);
            setTimeout(() => setAnimatePaymentError(false), 900);
        };
        const triggerPaymentSuccessAnimation = (msg) => {
            setSubmitSuccessMessage(msg || 'Pagamento confirmado com sucesso.');
            setAnimatePaymentSuccess(false);
            setTimeout(() => setAnimatePaymentSuccess(true), 10);
            setTimeout(() => setAnimatePaymentSuccess(false), 900);
        };

        const getActionApiConfig = () => {
            const apiUrl =
                process.env.REACT_APP_INSTALLMENTS_API_URL ||
                'https://75d4xc23kztwvkvo4mgknjus3e0bkwqv.lambda-url.us-east-1.on.aws/api/v1/action/execute';
            const apiKey =
                process.env.REACT_APP_INSTALLMENTS_API_KEY ||
                '123';
            return { apiUrl, apiKey };
        };

        const resolveRequestId = () => {
            // TESTE: forçar requestId fixo no ambiente local, mesmo se vier do conversation.
            // Para desligar depois, basta remover/alterar a env REACT_APP_FORCE_REQUEST_ID (ou REACT_APP_MOCK_REQUEST_ID).
            //const forcedRequestId ="000437909797";

            const candidates = [
                requestId,
                message?.requisicaoAgendamento,
                message?.context?.requestId,
                typeof window !== 'undefined' ? window.currentRequestId : null,
            ];
            const found = candidates.find((v) => v !== undefined && v !== null && String(v).trim() !== '');
            return found ? String(found).trim() : '';
        };

        const resolveIdFluxoAgendamento = () => {
            const mockIdFluxoAgendamento =
                (process.env.NODE_ENV === 'development' &&
                    (process.env.REACT_APP_MOCK_ID_FLUXO_AGENDAMENTO || 'c84d668f-e4d7-482c-906f-715fedf26cdc')) ||
                '';

            const fromMessagesHistory = (() => {
                try {
                    if (!Array.isArray(messages) || messages.length === 0) return null;
                    for (let i = messages.length - 1; i >= 0; i--) {
                        const m = messages[i];
                        const v = m?.idFluxoAgendamento ?? m?.context?.idFluxoAgendamento ?? null;
                        if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
                    }
                    return null;
                } catch (e) {
                    return null;
                }
            })();

            const candidates = [
                message?.idFluxoAgendamento,
                message?.context?.idFluxoAgendamento,
                message?.person?.idFluxoAgendamento,
                message?.person?.context?.idFluxoAgendamento,
                typeof window !== 'undefined' ? window.currentIdFluxoAgendamento : null,
                fromMessagesHistory,
                mockIdFluxoAgendamento
            ];
            const found = candidates.find((v) => v !== undefined && v !== null && String(v).trim() !== '');
            const resolved = found ? String(found).trim() : '';

            try {
                if (resolved && typeof window !== 'undefined') {
                    window.currentIdFluxoAgendamento = resolved;
                }
            } catch (e) {
                // ignore
            }

            return resolved;
        };

        const resolveCustomer = () => {
            const documentDigits = String(cpf || '').replace(/\D/g, '');
            const safeDocument = documentDigits.length === 11 ? documentDigits : '00000000000';

            // Quando o conversation passar a mandar, suportamos alguns possíveis paths/nomes.
            const fromConversation =
                message?.person?.customer ||
                message?.person?.custumer ||
                message?.customer ||
                message?.custumer ||
                message?.context?.customer ||
                message?.context?.custumer ||
                message?.context?.person?.customer ||
                message?.context?.person?.custumer ||
                (typeof window !== 'undefined' ? (window.currentCustomer || window.currentPerson?.customer || window.currentPerson?.custumer) : null) ||
                null;

            if (payerRelation === 'different') {
                return {
                    name: String(cardName || '').trim(),
                    document: safeDocument,
                    typeDocument: 'CPF',
                    email: fromConversation?.email || '',
                    phone: String(payerPhone || '').replace(/\D/g, ''),
                    birthDate: payerBirthDate || '',
                    ipAddress: fromConversation?.ipAddress || '',
                    address: {
                        street: String(addressStreet || ''),
                        number: String(addressNumber || ''),
                        complement: String(addressComplement || ''),
                        country: 'BR',
                        city: String(addressCity || ''),
                        state: String(addressState || ''),
                        zipCode: String(addressCep || '').replace(/\D/g, ''),
                        district: String(addressNeighborhood || '')
                    }
                };
            }

            if (fromConversation && typeof fromConversation === 'object') {
                // Regra: documento SEMPRE CPF, vindo do que o usuário digitou no form
                return {
                    ...fromConversation,
                    document: safeDocument,
                    typeDocument: 'CPF'
                };
            }

            // Mock em dev (e fallback em ausência de dados do conversation)
            return {
                name: String(cardName || 'Kaique Spagnol Tofoli').trim(),
                document: safeDocument,
                typeDocument: 'CPF',
                email: 'kaique.spagnol@gmail.com.br',
                phone: '11971599499',
                birthDate: '1998-01-01',
                ipAddress: '192.168.1.100',
                address: {
                    street: 'Avenida sete de setembro',
                    number: '4699',
                    country: 'BR',
                    city: 'Curitiba',
                    state: 'PR',
                    zipCode: '80240000',
                    district: 'Batel'
                }
            };
        };

        const callCheckRecordOpening = async (reqId) => {
            const { apiUrl, apiKey } = getActionApiConfig();

            const body = {
                context: {
                    action: 'CheckRecordOpeningSuccessAction',
                    requestId: reqId
                }
            };

            const resp = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': apiKey
                },
                body: JSON.stringify(body)
            });

            if (!resp.ok) {
                throw new Error(`Erro ao consultar ficha: ${resp.status}`);
            }

            return await resp.json();
        };

        const callGetPaymentCredentials = async (unitIdParam) => {
            const { apiUrl, apiKey } = getActionApiConfig();
            const brand = process.env.REACT_APP_CIELO_BRAND || '17';
            const unitId = String(
                unitIdParam ||
                (typeof window !== 'undefined' ? window.currentUnidadeSelecionada : '') ||
                ''
            ).trim();

            if (!unitId) {
                throw new Error('Unidade selecionada (unitId) ausente para buscar credenciais de pagamento.');
            }

            const body = {
                context: {
                    action: 'GetPaymentCredentiansAction',
                    brandId: brand,
                    unitId: unitId
                }
            };

            const resp = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': apiKey
                },
                body: JSON.stringify(body)
            });

            if (!resp.ok) {
                throw new Error(`Erro ao consultar credenciais de pagamento: ${resp.status}`);
            }

            const data = await resp.json();
            const merchantId = data?.context?.paymentCredencials?.merchantId ?? null;
            const merchantKey = data?.context?.paymentCredencials?.merchantKey ?? null;

            if (!merchantId || !merchantKey) {
                throw new Error('Credenciais de pagamento inválidas (merchantId/merchantKey ausentes).');
            }

            // Armazenar para próximos passos (evitar logar segredos)
            try {
                if (typeof window !== 'undefined') {
                    window.currentPaymentCredentials = {
                        brand: String(brand),
                        merchantId: String(merchantId),
                        merchantKey: String(merchantKey)
                    };
                }
            } catch (e) {
                // ignore
            }

            return {
                brand: String(brand),
                merchantId: String(merchantId),
                merchantKey: String(merchantKey)
            };
        };

        const normalizeCieloBrand = (brandKey) => {
            const raw = String(brandKey || '').toLowerCase();
            switch (raw) {
                case 'visa':
                    return 'Visa';
                case 'mastercard':
                    return 'Master';
                case 'amex':
                case 'americanexpress':
                case 'american-express':
                    return 'Amex';
                case 'elo':
                    return 'Elo';
                case 'discover':
                    return 'Discover';
                case 'jcb':
                    return 'JCB';
                // Hipercard não está na lista enviada (Visa / Master / Amex / Elo / Aura / JCB / Diners / Discover)
                // então tratamos como não suportado por enquanto.
                default:
                    return null;
            }
        };

        const toCieloExpirationDate = (value) => {
            const v = String(value || '').trim();
            // Aceita "MM/AA" (nosso input) e converte para "MM/YYYY"
            const m = v.match(/^(\d{2})\s*\/\s*(\d{2}|\d{4})$/);
            if (!m) return null;
            const mm = m[1];
            const yyOrYyyy = m[2];
            if (yyOrYyyy.length === 4) return `${mm}/${yyOrYyyy}`;
            return `${mm}/20${yyOrYyyy}`;
        };

        const callCreateCieloCardToken = async ({ merchantId, merchantKey }, customerName, cardData) => {
            const endpoint = process.env.REACT_APP_ENDPOINT_CIELO || 'https://apisandbox.cieloecommerce.cielo.com.br/1/card/';
            const url = endpoint.endsWith('/') ? endpoint : `${endpoint}/`;

            const resp = await fetch(url, {
                method: 'POST',
                headers: {
                    accept: 'application/json',
                    'Content-Type': 'application/json',
                    MerchantId: merchantId,
                    MerchantKey: merchantKey
                },
                body: JSON.stringify({
                    CustomerName: customerName,
                    CardNumber: cardData.cardNumber,
                    Holder: cardData.holder,
                    ExpirationDate: cardData.expirationDate,
                    Brand: cardData.brand
                })
            });

            if (!resp.ok) {
                throw new Error(`Erro ao tokenizar cartão na Cielo: ${resp.status}`);
            }

            const data = await resp.json();
            const cardToken = data?.CardToken ?? null;
            if (!cardToken || String(cardToken).trim() === '') {
                throw new Error('Resposta inválida da Cielo (CardToken ausente).');
            }

            try {
                if (typeof window !== 'undefined') {
                    window.currentCardToken = String(cardToken).trim();
                }
            } catch (e) {
                // ignore
            }

            return String(cardToken).trim();
        };

        const resolveIpAddress = async (customer) => {
            const fromCustomer = customer?.ipAddress;
            if (fromCustomer && String(fromCustomer).trim() !== '') {
                return String(fromCustomer).trim();
            }

            try {
                if (typeof window !== 'undefined' && window.currentIpAddress && String(window.currentIpAddress).trim() !== '') {
                    return String(window.currentIpAddress).trim();
                }
            } catch (e) {
                // ignore
            }

            const lookupUrl = process.env.REACT_APP_IP_LOOKUP_URL || 'https://api.ipify.org?format=json';
            try {
                const resp = await fetch(lookupUrl, { method: 'GET' });
                if (!resp.ok) throw new Error(`ip lookup status ${resp.status}`);
                const data = await resp.json();
                const ip = data?.ip || data?.ipAddress || null;
                if (!ip || String(ip).trim() === '') throw new Error('ip ausente');
                const resolved = String(ip).trim();

                try {
                    if (typeof window !== 'undefined') {
                        window.currentIpAddress = resolved;
                    }
                } catch (e) {
                    // ignore
                }

                return resolved;
            } catch (e) {
                // Fallback: se não conseguir obter IP público, mandamos um valor neutro
                return '0.0.0.0';
            }
        };

        const resolveMerchantOrderId = () => {
            const fromMessage = message?.unidadeSelecionadaNome ?? message?.context?.unidadeSelecionadaNome ?? null;

            const fromMessagesHistory = (() => {
                try {
                    if (!Array.isArray(messages) || messages.length === 0) return null;
                    for (let i = messages.length - 1; i >= 0; i--) {
                        const m = messages[i];
                        const v = m?.unidadeSelecionadaNome ?? m?.context?.unidadeSelecionadaNome ?? null;
                        if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
                    }
                    return null;
                } catch (e) {
                    return null;
                }
            })();

            const fromWindow = (() => {
                try {
                    if (typeof window === 'undefined') return null;
                    const v = window.currentUnidadeSelecionadaNome;
                    return v !== undefined && v !== null && String(v).trim() !== '' ? String(v).trim() : null;
                } catch (e) {
                    return null;
                }
            })();

            const candidates = [fromMessage, fromMessagesHistory, fromWindow];
            const found = candidates.find((v) => v !== undefined && v !== null && String(v).trim() !== '');
            return found ? String(found).trim() : '';
        };

        const callMakePaymentAction = async (payload) => {
            const { apiUrl, apiKey } = getActionApiConfig();
            const resp = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': apiKey
                },
                body: JSON.stringify(payload)
            });

            if (!resp.ok) {
                throw new Error(`Erro ao processar pagamento (MakePaymentAction): ${resp.status}`);
            }

            const data = await resp.json();

            try {
                if (typeof window !== 'undefined') {
                    window.currentMakePaymentResponse = data;
                }
            } catch (e) {
                // ignore
            }

            return data;
        };

        const normalizeBirthDateToIso = (value) => {
            const v = String(value || '').trim();
            if (!v) return '';

            // Aceita "DD/MM/YYYY"
            let m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
            if (m) return `${m[3]}-${m[2]}-${m[1]}`;

            // Aceita "DD-MM-YYYY"
            m = v.match(/^(\d{2})-(\d{2})-(\d{4})$/);
            if (m) return `${m[3]}-${m[2]}-${m[1]}`;

            // Aceita "YYYY-MM-DD" (já ISO)
            m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/);
            if (m) return v;

            // Fallback: tenta Date.parse (último recurso)
            const d = new Date(v);
            if (Number.isFinite(d.getTime())) {
                const yyyy = String(d.getFullYear()).padStart(4, '0');
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                return `${yyyy}-${mm}-${dd}`;
            }

            return '';
        };

        const pollFicha = async (reqId) => {
            for (let attempt = 1; attempt <= 3; attempt++) {
                const data = await callCheckRecordOpening(reqId);
                const numeroFicha = data?.context?.numeroFicha ?? null;

                console.log('CheckRecordOpeningSuccessAction:', {
                    attempt,
                    requestId: reqId,
                    numeroFicha,
                    recordOpeningSuccess: data?.context?.recordOpeningSuccess
                });

                if (numeroFicha !== null && numeroFicha !== undefined && String(numeroFicha).trim() !== '') {
                    const numeroFichaRaw = String(numeroFicha).trim();
                    const digits = numeroFichaRaw.replace(/\D/g, '');

                    const unidadeSelecionada = digits.length >= 3 ? digits.slice(0, 3) : '';
                    const fichaId = digits.length > 3 ? digits.slice(3) : digits;

                    try {
                        if (typeof window !== 'undefined') {
                            if (unidadeSelecionada) window.currentUnidadeSelecionada = unidadeSelecionada;
                            if (fichaId) window.currentFichaId = fichaId;
                        }
                    } catch (e) {
                        // ignore
                    }

                    return {
                        numeroFichaRaw,
                        unidadeSelecionada,
                        fichaId,
                        raw: data
                    };
                }

                if (attempt < 3) {
                    await sleep(2000);
                }
            }

            return null;
        };

        const run = async () => {
            setIsSubmitting(true);

            // (por enquanto) mantemos os dados preparados para os próximos passos
            const paymentData = {
                cardHolderName: cardName.trim(),
                cardNumber: cardNumber.replace(/\D/g, ''),
                cpf: cpf.replace(/\D/g, ''),
                expiry: expiry,
                cvv: cvv.replace(/\D/g, ''),
                installments: Number(selectedInstallments) || 1,
                monetaryAmount: monetaryAmount,
                client_id,
                marca,
                origem,
                localizacao,
                operational_system,
                version_app
            };

            let handled = false;
            const sendPaymentFailed = () => {
                if (handled) return;
                handled = true;
                triggerPaymentErrorAnimation('Não foi possível processar o pagamento. Tente novamente.');
                try {
                    sendMessage(
                        setMessages,
                        client_id,
                        'payment-failed',
                        messages,
                        marca,
                        origem,
                        null,
                        localizacao,
                        null,
                        null,
                        null,
                        null,
                        null,
                        operational_system,
                        version_app,
                        null,
                        null,
                        requestImage,
                        setRequestId
                    );
                } catch (e) {
                    // ignore
                }
                // No modo resultado, não fechar automaticamente (usuário fecha manualmente)
            };

            const sendFichaNaoCarregada = () => {
                if (handled) return;
                handled = true;
                triggerPaymentErrorAnimation('Não foi possível processar o pagamento. Tente novamente.');
                try {
                    sendMessage(
                        setMessages,
                        client_id,
                        'ficha-nao-carregada',
                        messages,
                        marca,
                        origem,
                        null,
                        localizacao,
                        null,
                        null,
                        null,
                        null,
                        null,
                        operational_system,
                        version_app,
                        null,
                        null,
                        requestImage,
                        setRequestId
                    );
                } catch (e) {
                    // ignore
                }
                // No modo resultado, não fechar automaticamente (usuário fecha manualmente)
            };

            const sendPaymentSuccessful = () => {
                if (handled) return;
                handled = true;
                triggerPaymentSuccessAnimation('Pagamento confirmado com sucesso.');
                try {
                    sendMessage(
                        setMessages,
                        client_id,
                        'payment-successful',
                        messages,
                        marca,
                        origem,
                        null,
                        localizacao,
                        null,
                        null,
                        null,
                        null,
                        null,
                        operational_system,
                        version_app,
                        null,
                        null,
                        requestImage,
                        setRequestId
                    );
                } catch (e) {
                    // ignore
                }
                // No modo resultado, não fechar automaticamente (usuário fecha manualmente)
            };

            let fichaConfirmada = false;

            try {
                const reqId = resolveRequestId();

                if (!reqId) {
                    console.warn('RequestId (requisicaoAgendamento) não encontrado; disparando fallback ficha-nao-carregada.');
                    sendFichaNaoCarregada();
                    return;
                }

                const ficha = await pollFicha(reqId);

                if (!ficha) {
                    // Se nos 3 casos vier null, chamar conversation com input fixo
                    sendFichaNaoCarregada();
                    return;
                }

                fichaConfirmada = true;

                // Iniciar pagamento (por enquanto, apenas montando e logando payload; a próxima API entra aqui)
                const idFluxoAgendamento = resolveIdFluxoAgendamento();
                const customer = resolveCustomer();
                const paymentCredentials = await callGetPaymentCredentials(ficha?.unidadeSelecionada);

                const cieloBrand = normalizeCieloBrand(cardBrand);
                const expirationDate = toCieloExpirationDate(expiry);
                if (!cieloBrand) {
                    throw new Error('Bandeira do cartão não suportada para tokenização na Cielo.');
                }
                if (!expirationDate) {
                    throw new Error('Validade do cartão inválida para tokenização na Cielo.');
                }

                const cardToken = await callCreateCieloCardToken(
                    { merchantId: paymentCredentials.merchantId, merchantKey: paymentCredentials.merchantKey },
                    String(customer?.name || 'Cliente').trim(),
                    {
                        cardNumber: String(paymentData.cardNumber || ''),
                        holder: String(cardName || '').trim(),
                        expirationDate,
                        brand: cieloBrand
                    }
                );

                const ipAddress = await resolveIpAddress(customer);
                const merchantOrderId = resolveMerchantOrderId();

                const amountCents = (() => {
                    const n = Number(paymentData.monetaryAmount);
                    if (!Number.isFinite(n) || n <= 0) return 0;
                    return Math.round(n * 100);
                })();

                const custumer = {
                    ...customer,
                    // regra: sempre CPF do form (já sobrescrevemos em resolveCustomer)
                    document: String(customer?.document || '').trim(),
                    typeDocument: 'CPF',
                    birthDate: normalizeBirthDateToIso(customer?.birthDate),
                    ipAddress,
                    payment: {
                        type: 'CreditCard',
                        installments: Number(paymentData.installments) || 1,
                        amount: amountCents,
                        capture: true,
                        softDescriptor: 'Laboratorios Amais Realizacao de procedimentos',
                        creditCard: {
                            cardToken: cardToken,
                            securityCode: String(paymentData.cvv || '').trim(),
                            brand: cieloBrand
                        },
                        extradata: [
                            { name: 'fichaId', value: String(ficha.fichaId) },
                            { name: 'requestId', value: String(reqId) }
                        ]
                    }
                };

                const makePaymentBody = {
                    context: {
                        action: 'MakePaymentAction',
                        brand: process.env.REACT_APP_CIELO_BRAND || paymentCredentials.brand || 'Amais',
                        merchantOrderId,
                        unitId: ficha?.unidadeSelecionada,
                        requestId: reqId,
                        agendamentoId: idFluxoAgendamento,
                        custumer
                    }
                };

                const callGetPaymentStatus = async (requestIdForStatus) => {
                    const { apiUrl, apiKey } = getActionApiConfig();
                    const resp = await fetch(apiUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'x-api-key': apiKey
                        },
                        body: JSON.stringify({
                            context: {
                                action: 'GetPaymentStatusAction',
                                requestId: requestIdForStatus
                            }
                        })
                    });

                    if (!resp.ok) {
                        throw new Error(`Erro ao consultar status do pagamento: ${resp.status}`);
                    }

                    return await resp.json();
                };

                const isSuccessResponse = (resp) => {
                    const okStatus = resp?.status === 'success';
                    const okReturnCode = resp?.returnCode === 0 || resp?.returnCode === '0';
                    const okActionResult =
                        resp?.context?.actionResult?.success === true ||
                        resp?.context?.actionResult?.success === undefined;
                    return okStatus && okReturnCode && okActionResult;
                };

                const getPaymentStatusValue = (resp) => {
                    // Novo formato: context.paymentStatusApiResponse.data[0].paymentStatus
                    const fromApiResponse = resp?.context?.paymentStatusApiResponse?.data?.[0]?.paymentStatus;
                    if (fromApiResponse !== undefined && fromApiResponse !== null && String(fromApiResponse).trim() !== '') {
                        return String(fromApiResponse).trim();
                    }
                    // Formato antigo/alternativo: context.paymentStatus
                    const fallback = resp?.context?.paymentStatus;
                    if (fallback !== undefined && fallback !== null && String(fallback).trim() !== '') {
                        return String(fallback).trim();
                    }
                    return null;
                };

                const pollPaymentStatus = async (requestIdForStatus) => {
                    for (let attempt = 1; attempt <= 3; attempt++) {
                        const statusResp = await callGetPaymentStatus(requestIdForStatus);
                        const paymentStatus = getPaymentStatusValue(statusResp);

                        // Só consideramos sucesso quando o status do pagamento for PaymentConfirmed
                        if (isSuccessResponse(statusResp) && paymentStatus === 'PaymentConfirmed') {
                            return { ok: true, statusResp };
                        }

                        console.log('⏳ GetPaymentStatusAction ainda não confirmou sucesso:', {
                            attempt,
                            status: statusResp?.status,
                            returnCode: statusResp?.returnCode,
                            paymentStatus
                        });

                        if (attempt < 3) {
                            await sleep(5000);
                        }
                    }

                    return { ok: false };
                };

                // Processar pagamento (sem logar dados sensíveis)
                let makePaymentResp = null;
                try {
                    makePaymentResp = await callMakePaymentAction(makePaymentBody);
                } catch (makePaymentErr) {
                    console.error('❌ Erro no MakePaymentAction:', makePaymentErr);
                    // Se falhar o processamento do pagamento, avisar o conversation
                    sendPaymentFailed();
                    return;
                }

                // Cenário: API respondeu 200, mas com status de erro (ex.: status === "error", returnCode != 0, actionResult.success === false)
                const makePaymentOk = isSuccessResponse(makePaymentResp);

                if (!makePaymentOk) {
                    console.error('❌ MakePaymentAction retornou erro (lógico):', {
                        status: makePaymentResp?.status,
                        returnCode: makePaymentResp?.returnCode,
                        actionResult: makePaymentResp?.context?.actionResult
                    });

                    sendPaymentFailed();
                    return;
                }

                const iniciarPagamentoPayload = {
                    requestId: reqId,
                    idFluxoAgendamento,
                    fichaId: ficha.fichaId,
                    unidadeSelecionada: ficha.unidadeSelecionada,
                    customer,
                    paymentCredentials: { brand: paymentCredentials.brand, merchantId: paymentCredentials.merchantId },
                    cardToken,
                    paymentData
                };

                console.log('✅ Credenciais Cielo obtidas e prontas para próximos passos:', {
                    brand: paymentCredentials.brand,
                    merchantId: paymentCredentials.merchantId
                });
                console.log('✅ CardToken Cielo gerado e salvo para o próximo passo.');
                console.log('✅ Pagamento processado via MakePaymentAction.', {
                    status: makePaymentResp?.status,
                    returnCode: makePaymentResp?.returnCode
                });
                console.log('✅ Ficha carregada, iniciando pagamento (payload mock/pronto p/ API):', iniciarPagamentoPayload);

                // Polling de status do pagamento
                const statusResult = await pollPaymentStatus(reqId);
                if (!statusResult.ok) {
                    sendPaymentFailed();
                    return;
                }

                console.log('✅ GetPaymentStatusAction confirmou pagamento com sucesso.');
                sendPaymentSuccessful();
            } catch (e) {
                console.error('❌ Erro no fluxo de pagamento (check ficha):', e);
                // Qualquer erro em APIs do pagamento deve avisar payment-failed,
                // exceto os cenários explícitos de ficha-nao-carregada (já tratados acima).
                if (!handled) {
                    sendPaymentFailed();
                }
            } finally {
                setIsSubmitting(false);
            }
        };

        run();
    };

    if (!isModalOpen) {
        return null;
    }

    return (
        <>
            <div
                className={`modal-overlay ${visible ? 'visible' : ''}`}
                onClick={handleUserClose}
            ></div>
            <div
                className={`modal-content-csat modal-content-payment ${visible ? 'visible' : ''} ${animatePaymentError ? 'payment-error-animate' : ''} ${animatePaymentSuccess ? 'payment-success-animate' : ''}`}
            >
                <div className="modal-header">
                    <h3>Pagamento</h3>
                    <button
                        className="close-button"
                        onClick={handleUserClose}
                        aria-label="Fechar modal de pagamento"
                    >
                        ×
                    </button>
                </div>

                <div className={`modal-body payment-body ${(submitSuccessMessage || submitErrorMessage) ? 'payment-body--result' : ''}`}>
                    {submitSuccessMessage || submitErrorMessage ? (
                        <div
                            className={`payment-result ${submitSuccessMessage ? 'payment-result--success' : 'payment-result--error'}`}
                            role={submitSuccessMessage ? 'status' : 'alert'}
                            aria-live="polite"
                        >
                            <div className="payment-result-icon" aria-hidden="true" />
                            <div className="payment-result-title">
                                {submitSuccessMessage ? 'Pagamento confirmado' : 'Pagamento não concluído'}
                            </div>
                            <div className="payment-result-message">
                                {submitSuccessMessage || submitErrorMessage}
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Step 0: pergunta se o pagador é o próprio paciente */}
                            {payerRelation === null && !isAddressStep && (
                                <div className="payment-payer-step">
                                    <p className="payment-payer-question">
                                        Quem vai fazer o procedimento é a mesma pessoa que vai pagar?
                                    </p>
                                    <div className="payment-payer-buttons">
                                        <button
                                            type="button"
                                            className="payment-payer-button"
                                            onClick={() => setPayerRelation('same')}
                                        >
                                            Sim
                                        </button>
                                        <button
                                            type="button"
                                            className="payment-payer-button secondary"
                                            onClick={() => setPayerRelation('different')}
                                        >
                                            Não
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Step 1: formulário de cartão (pagador = paciente ou não) */}
                            {payerRelation !== null && !isAddressStep && (
                                <>
                                    <div className="payment-card-preview">
                                        <div className={`payment-card ${cardBrand ? `payment-card--${cardBrand}` : 'payment-card--default'}`}>
                                            <div className="payment-card-top-row">
                                                <div className="payment-card-chip">
                                                    <img
                                                        src={process.env.PUBLIC_URL + '/chip.png'}
                                                        alt="Chip do cartão"
                                                        className="payment-card-chip-img"
                                                    />
                                                </div>
                                                <div className="payment-card-brand">
                                                    {cardBrand && getCardLogoSrc() ? (
                                                        <img
                                                            src={getCardLogoSrc()}
                                                            alt={getCardBrandLabel()}
                                                            className="payment-card-logo"
                                                        />
                                                    ) : (
                                                        <span>{getCardBrandLabel()}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="payment-card-number">
                                                {displayCardNumber()}
                                            </div>
                                            <div className="payment-card-bottom-row">
                                                <div className="payment-card-holder-block">
                                                    <span className="payment-card-label">NOME NO CARTÃO</span>
                                                    <span className="payment-card-value">{displayName()}</span>
                                                </div>
                                                <div className="payment-card-expiry-block">
                                                    <span className="payment-card-label">VALIDADE</span>
                                                    <span className="payment-card-value">{displayExpiry()}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="payment-form">
                                        <div className="payment-input-group">
                                            <label htmlFor="cardName">Nome como está no cartão</label>
                                            <input
                                                id="cardName"
                                                type="text"
                                                placeholder="Digite o nome do titular"
                                                value={cardName}
                                                onChange={(e) => setCardName(e.target.value)}
                                                autoComplete="cc-name"
                                            />
                                        </div>

                                        <div className="payment-input-group">
                                            <label htmlFor="cardNumber">Número do cartão</label>
                                            <input
                                                id="cardNumber"
                                                type="text"
                                                placeholder="•••• •••• •••• ••••"
                                                value={cardNumber}
                                                onChange={(e) => setCardNumber(formatCardNumberForInput(e.target.value))}
                                                inputMode="numeric"
                                                autoComplete="cc-number"
                                            />
                                        </div>

                                        <div className="payment-input-row">
                                            <div className="payment-input-group">
                                                <label htmlFor="cpf">CPF do titular</label>
                                                <input
                                                    id="cpf"
                                                    type="text"
                                                    placeholder="Somente números"
                                                    value={cpf}
                                                    onChange={(e) => setCpf(formatCpfForInput(e.target.value))}
                                                    inputMode="numeric"
                                                />
                                            </div>

                                            <div className="payment-input-group">
                                                <label htmlFor="expiry">Validade (MM/AA)</label>
                                                <input
                                                    id="expiry"
                                                    type="text"
                                                    placeholder="MM/AA"
                                                    value={expiry}
                                                    onChange={(e) => setExpiry(formatExpiryForInput(e.target.value))}
                                                    inputMode="numeric"
                                                    autoComplete="cc-exp"
                                                />
                                            </div>

                                            <div className="payment-input-group">
                                                <label htmlFor="cvv">CVV</label>
                                                <input
                                                    id="cvv"
                                                    type="password"
                                                    placeholder="3 ou 4 dígitos"
                                                    value={cvv}
                                                    onChange={(e) => setCvv(formatCvvForInput(e.target.value))}
                                                    inputMode="numeric"
                                                    autoComplete="cc-csc"
                                                />
                                            </div>
                                        </div>

                                        {/* Campo extra de data de nascimento quando o pagador NÃO é o paciente */}
                                        {payerRelation === 'different' && (
                                            <>
                                                <div className="payment-input-group">
                                                    <label htmlFor="payerBirthDate">Data de nascimento do pagador</label>
                                                    <input
                                                        id="payerBirthDate"
                                                        type="text"
                                                        placeholder="DD/MM/AAAA"
                                                        value={payerBirthDate}
                                                        onChange={(e) => setPayerBirthDate(formatBirthDateForInput(e.target.value))}
                                                    />
                                                </div>

                                                <div className="payment-input-group">
                                                    <label htmlFor="payerPhone">Telefone do pagador</label>
                                                    <input
                                                        id="payerPhone"
                                                        type="text"
                                                        placeholder="(11) 99999-9999"
                                                        value={payerPhone}
                                                        onChange={(e) => setPayerPhone(formatPhoneForInput(e.target.value))}
                                                        inputMode="tel"
                                                    />
                                                </div>
                                            </>
                                        )}

                                        {/* Último campo: parcelas */}
                                        <div className="payment-input-group">
                                            <label htmlFor="installments">Parcelas</label>
                                            <div
                                                className={`payment-dropdown ${installmentsOpen ? 'open' : ''}`}
                                                ref={installmentsDropdownRef}
                                            >
                                                <button
                                                    id="installments"
                                                    type="button"
                                                    className={`payment-dropdown-control ${installmentsLoading ? 'loading' : ''}`}
                                                    onClick={() => {
                                                        if (installmentsLoading || !monetaryAmount) return;
                                                        setInstallmentsOpen((v) => !v);
                                                    }}
                                                    disabled={installmentsLoading || !monetaryAmount}
                                                    aria-expanded={installmentsOpen}
                                                >
                                                    {selectedInstallmentsLabel}
                                                    <span className="payment-caret" aria-hidden="true" />
                                                </button>

                                                {installmentsOpen && !installmentsLoading && monetaryAmount && (
                                                    <div className="payment-dropdown-menu" role="listbox" aria-label="Opções de parcelamento">
                                                        <div className="payment-dropdown-list">
                                                            {(installmentsOptions.length > 0
                                                                ? installmentsOptions
                                                                : [{ count: 1, label: `1x de ${formatBRL(monetaryAmount)}` }]
                                                            ).map((opt) => (
                                                                <div
                                                                    key={opt.count}
                                                                    className={`payment-dropdown-item ${Number(selectedInstallments) === opt.count ? 'selected' : ''}`}
                                                                    onClick={() => {
                                                                        setSelectedInstallments(opt.count);
                                                                        setInstallmentsOpen(false);
                                                                    }}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                >
                                                                    {opt.label}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {installmentsLoading && (
                                                <div className="payment-help-text">Carregando opções de parcelamento…</div>
                                            )}
                                            {!installmentsLoading && installmentsError && (
                                                <div className="payment-error-text">{installmentsError}</div>
                                            )}
                                            {!installmentsLoading && !installmentsError && monetaryAmount && (
                                                <div className="payment-help-text">Total: {formatBRL(monetaryAmount)}</div>
                                            )}
                                            {!monetaryAmount && (
                                                <div className="payment-error-text">Valor do pagamento indisponível.</div>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Step 2: endereço do pagador (apenas quando pagador != paciente) */}
                            {payerRelation === 'different' && isAddressStep && (
                                <div className="payment-address-form">
                                    <h4 className="payment-address-title">Dados de endereço do pagador</h4>

                                    <div className="payment-input-group payment-cep-group">
                                        <label htmlFor="addressCep">CEP</label>
                                        <div className="payment-cep-row">
                                            <input
                                                id="addressCep"
                                                type="text"
                                                placeholder="Somente números"
                                                value={addressCep}
                                                onChange={(e) => {
                                                    setAddressCep(e.target.value);
                                                    setCepError('');
                                                }}
                                                inputMode="numeric"
                                            />
                                            <button
                                                type="button"
                                                className="payment-cep-button"
                                                onClick={handleCepLookup}
                                                disabled={cepLoading}
                                            >
                                                {cepLoading ? 'Buscando...' : 'Buscar CEP'}
                                            </button>
                                        </div>
                                        {cepError && (
                                            <div className="payment-error-text">{cepError}</div>
                                        )}
                                    </div>

                                    <div className="payment-input-group">
                                        <label htmlFor="addressStreet">Endereço</label>
                                        <input
                                            id="addressStreet"
                                            type="text"
                                            placeholder="Rua, avenida, etc."
                                            value={addressStreet}
                                            onChange={(e) => setAddressStreet(e.target.value)}
                                        />
                                    </div>

                                    <div className="payment-input-row">
                                        <div className="payment-input-group">
                                            <label htmlFor="addressNumber">Número</label>
                                            <input
                                                id="addressNumber"
                                                type="text"
                                                placeholder="Número"
                                                value={addressNumber}
                                                onChange={(e) => setAddressNumber(e.target.value)}
                                            />
                                        </div>
                                        <div className="payment-input-group">
                                            <label htmlFor="addressComplement">Complemento</label>
                                            <input
                                                id="addressComplement"
                                                type="text"
                                                placeholder="Apartamento, bloco, referência (opcional)"
                                                value={addressComplement}
                                                onChange={(e) => setAddressComplement(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="payment-input-row">
                                        <div className="payment-input-group">
                                            <label htmlFor="addressNeighborhood">Bairro</label>
                                            <input
                                                id="addressNeighborhood"
                                                type="text"
                                                placeholder="Bairro"
                                                value={addressNeighborhood}
                                                onChange={(e) => setAddressNeighborhood(e.target.value)}
                                            />
                                        </div>
                                        <div className="payment-input-group">
                                            <label htmlFor="addressCity">Cidade</label>
                                            <input
                                                id="addressCity"
                                                type="text"
                                                placeholder="Cidade"
                                                value={addressCity}
                                                onChange={(e) => setAddressCity(e.target.value)}
                                            />
                                        </div>
                                        <div className="payment-input-group">
                                            <label htmlFor="addressState">Estado</label>
                                            <input
                                                id="addressState"
                                                type="text"
                                                placeholder="UF"
                                                value={addressState}
                                                onChange={(e) => setAddressState(e.target.value.toUpperCase())}
                                                maxLength={2}
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>

                {!(submitSuccessMessage || submitErrorMessage) && (
                    <div className="modal-footer">
                        {payerRelation === null ? null : (
                            payerRelation === 'different' && !isAddressStep ? (
                                <button
                                    className={`payment-confirm-button payment-next-button ${isSubmitting ? 'loading' : ''}`}
                                    type="button"
                                    onClick={() => {
                                        if (!isFirstStepValid() || isSubmitting) return;
                                        setIsAddressStep(true);
                                    }}
                                    disabled={!isFirstStepValid() || isSubmitting}
                                    aria-label="Avançar para endereço do pagador"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="loading-spinner"></div>
                                            <span>Processando...</span>
                                        </>
                                    ) : (
                                        'Avançar →'
                                    )}
                                </button>
                            ) : (
                                <button
                                    className={`payment-confirm-button ${isSubmitting ? 'loading' : ''}`}
                                    onClick={handleSubmit}
                                    disabled={
                                        !isFormValid() ||
                                        isSubmitting ||
                                        (payerRelation === 'different' && isAddressStep && !isAddressValid())
                                    }
                                    aria-label="Realizar pagamento"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="loading-spinner"></div>
                                            <span>Processando pagamento...</span>
                                        </>
                                    ) : (
                                        'Realizar pagamento'
                                    )}
                                </button>
                            )
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default Pagamento;